import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid
} from '@mui/material';
import { useGame } from '../../contexts/GameContext';
import triviaQuestions from '../../data/trivia_questions.json';
import BaseRound from './BaseRound';

const difficultyColors = {
  easy: '#4caf50',    // Grün
  medium: '#ff9800',  // Gelb
  hard: '#f44336'     // Rot
};

const difficultyLabels = {
  easy: 'Leicht',
  medium: 'Mittel',
  hard: 'Schwer'
};

const difficultyPoints = {
  easy: 1,
  medium: 2,
  hard: 3
};

const difficultyPunishment = {
  easy: 3,    // Leichte Frage falsch = 3 Schlücke
  medium: 2,  // Mittlere Frage falsch = 2 Schlücke
  hard: 1     // Schwere Frage falsch = 1 Schluck
};

export default function QuestionRound() {
  const { state } = useGame();
  const [randomQuestions, setRandomQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [shuffledAnswers, setShuffledAnswers] = useState([]);

  // Gruppiere und wähle Fragen nur beim ersten Render
  useEffect(() => {
    const questionsByCategory = triviaQuestions.reduce((acc, question) => {
      if (!acc[question.category]) {
        acc[question.category] = [];
      }
      acc[question.category].push(question);
      return acc;
    }, {});

    const categories = Object.keys(questionsByCategory);
    const shuffledCategories = [...categories].sort(() => 0.5 - Math.random());
    const selectedCategories = shuffledCategories.slice(0, 4);

    const selected = selectedCategories.map(category => {
      const categoryQuestions = questionsByCategory[category];
      return categoryQuestions[Math.floor(Math.random() * categoryQuestions.length)];
    });

    setRandomQuestions(selected);
  }, []);

  const handleQuestionSelect = (question) => {
    // Mische die Antworten nur einmal beim Auswählen der Frage
    const answers = question.type === 'multiple'
      ? [...question.incorrect_answers, question.correct_answer]
      : ['WAHR', 'FALSCH'];
    
    // Mische das Array einmal mit Fisher-Yates Algorithmus
    const shuffled = [...answers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    setShuffledAnswers(shuffled);
    setSelectedQuestion(question);
    setSelectedAnswer(null);
    setShowResult(false);
  };

  const handleAnswerSelect = (answer) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleConfirmAnswer = () => {
    const correct = selectedAnswer === selectedQuestion.correct_answer;
    setIsCorrect(correct);
    setShowResult(true);
  };

  const resultData = showResult ? {
    title: isCorrect 
      ? selectedQuestion.question
      : `${selectedQuestion.question}\n\nRichtige Antwort: ${selectedQuestion.correct_answer}`,
    success: isCorrect,
    drinks: isCorrect 
      ? difficultyPoints[selectedQuestion.difficulty]
      : difficultyPunishment[selectedQuestion.difficulty],
    player: state.activePlayer,
    points: isCorrect ? difficultyPoints[selectedQuestion.difficulty] : 0,
  } : null;

  if (!selectedQuestion) {
    return (
      <BaseRound
        instructions="Wähle eine Kategorie aus Trivia-Fragen!"
        showResult={false}
      >
        <Grid container spacing={2}>
          {randomQuestions.map((question) => (
            <Grid item xs={12} sm={6} key={question.category}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleQuestionSelect(question)}
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  gap: 1,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <Typography variant="h6" component="div">
                  {question.category}
                </Typography>
                <Chip
                  label={difficultyLabels[question.difficulty]}
                  sx={{
                    bgcolor: difficultyColors[question.difficulty],
                    color: 'white'
                  }}
                  size="small"
                />
              </Button>
            </Grid>
          ))}
        </Grid>
      </BaseRound>
    );
  }

  return (
    <BaseRound
      instructions={showResult ? null : "Wähle deine Antwort:"}
      showResult={showResult}
      resultData={resultData}
    >
      <Card sx={{ bgcolor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              {selectedQuestion.category}
            </Typography>
            <Chip
              label={difficultyLabels[selectedQuestion.difficulty]}
              sx={{
                bgcolor: difficultyColors[selectedQuestion.difficulty],
                color: 'white'
              }}
              size="small"
            />
          </Box>

          <Typography variant="h5" gutterBottom>
            {selectedQuestion.question}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 3 }}>
            {shuffledAnswers.map((answer) => (
              <Button
                key={answer}
                variant={selectedAnswer === answer ? "contained" : "outlined"}
                onClick={() => handleAnswerSelect(answer)}
                disabled={showResult}
                sx={{
                  p: 2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    bgcolor: !showResult && 'action.hover'
                  }
                }}
              >
                <Typography variant="body1">
                  {answer}
                </Typography>
              </Button>
            ))}
          </Box>

          {selectedAnswer && !showResult && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmAnswer}
              fullWidth
              sx={{ mt: 3 }}
            >
              Antwort bestätigen
            </Button>
          )}
        </CardContent>
      </Card>
    </BaseRound>
  );
} 